import { ChangeDetectorRef, Component, ComponentFactoryResolver, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import * as blocks from '@blocks/blocks';
import { HomepageService } from '@services/homepage.service';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { SharedService } from '@services/shared.service';
import { environment } from '@environments/environment';
import { MetaTagsService } from '@app/_services/meta-tags.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TranslateService } from '@ngx-translate/core';
import { SiteService } from '@app/_services/site.service';
import { BlockGridComponent } from '../../blocks/block-grid/block-grid.component';
import { NgxJsonLdModule } from '@ngx-lite/json-ld';
import { NgIf } from '@angular/common';
import { TestService } from '@app/_services/test';

@Component({
    selector: 'app-home',
    templateUrl: './page-home-one.component.html',
    styleUrls: ['./page-home-one.component.scss'],
    standalone: true,
    imports: [NgIf, NgxJsonLdModule, BlockGridComponent]
})
export class PageHomeOneComponent implements OnInit, OnDestroy {
    schema;

    destroy$: Subject<void> = new Subject<void>();

    layout;
    blocks = [];
    agent = { mobile: true, desktop: true };

    constructor(
        private sharedService: SharedService,
        private homepageService: HomepageService,
        private router: Router,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        public translate: TranslateService,
        private metaTagsService: MetaTagsService,
        private deviceDetector: DeviceDetectorService,
        public siteService: SiteService,
        private testService: TestService

    ) {
        this.router.onSameUrlNavigation = 'ignore';
        this.agent.desktop = this.deviceDetector.isDesktop() || this.deviceDetector.isTablet();
        this.agent.mobile = this.deviceDetector.isMobile();
    }

    ngOnInit() {
        // this.testService.getAllTest().subscribe(response => {
        //     console.log("Test response: ", response);   
        // });
        this.sharedService.pageSubject$.next("homepage");
        this.sharedService.sectorSubject$.next(null);

        let layoutId = this.route.snapshot.queryParamMap.get('layoutId');
        this.homepageService.getLayout(layoutId).subscribe(response => {
            this.layout = response.layout?.layout;
            this.metaTagsService.setMetaTags({
                title: response.homepage?.seo?.title?.[this.translate.currentLang || 'en'] || this.siteService.siteSetting?.name || "",
                description: response.homepage?.seo?.description?.[this.translate.currentLang || 'en'],
            });

        });

        this.schema = {
            "@context": "http://schema.org",
            "@type": "WebSite",
            "url": environment.web,
            "potentialAction":
            {
                "@type": "SearchAction",
                "target": `${environment.web}/en/search?q={search_term_string}`,
                "query": "required",
                "query-input": "required name=search_term_string"
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    buildLayout(item?) {
        _.forEach(item || this.layout.children, element => {
            let ref = this.getContainerFor(element._id);

            if (ref && element.type && blocks[element.type]) {
                const componentRef = ref.createComponent(blocks[element.type]);
                if (element.type == "BlockSlideshowComponent") {
                    componentRef.instance.bannerItems = element.data;
                    componentRef.instance.fullWidth = element.fullWidth || false;
                    componentRef.instance.withDepartments = false;
                } else if (element.type == "BlockSlideshowWithButtonComponent") {
                    componentRef.instance.slides = element.slides;
                    /* componentRef.instance.withDepartments = false; */
                }
                else if (element.type == "BlockMiniFeatureCardsComponent") {
                    componentRef.instance.cards = element.cards;
                }
                else if (element.type == "BlockStoresComponent" || element.type == "BlockStoresNewComponent") {
                    componentRef.instance.companies = element.data;
                    componentRef.instance.loop = element.loop == "true";
                } else if (element.type == "BlockProductsCarouselComponent" || element.type == "BlockProductsCarouselNewComponent") {
                    componentRef.instance.name = element.name;
                    componentRef.instance.products = element.data;
                    componentRef.instance.tag = element.tag;
                    componentRef.instance.headerIcon = element.headerIcon
                    componentRef.instance.rows = +element.rows || 1;
                    componentRef.instance.layout = element.layout || 'custom';

                } else if (element.type == "BlockRealEstateCarouselComponent") {
                    componentRef.instance.name = element.name;
                    componentRef.instance.products = element.data;
                    componentRef.instance.tag = element.tag;
                    componentRef.instance.rows = 1;
                    componentRef.instance.icon = element.icon;
                    componentRef.instance.mode = element.mode;
                }
                else if (element.type == "BlockAdvancedSearchComponent") {
                    componentRef.instance.searchInfo = { rows: element?.rows, categoryIds: element.categoryIds, sector: element.sectorSlug, data: element.data };
                    //componentRef.instance.blockId = element._id;
                    componentRef.instance.subtypeId = element.subtypeId;
                    componentRef.instance.type = element.ElementType;

                }
                else if (element.type == "BlockFeaturesComponent") {
                    componentRef.instance.options = element.data;
                }
                else if (element.type == "BlockVideoPlayerComponent") {
                    componentRef.instance.src = element.data.src;
                    componentRef.instance.type = element.data.type;
                    componentRef.instance.provider = element.data.provider;
                }
                else if (element.type == "BlockProductsComponent") {
                    if (element.data?.length == 7) {
                        componentRef.instance.header = element.name;
                        componentRef.instance.products = element.data;
                        componentRef.instance.layout = element.layout || 'large-first';
                    }
                }
                else if (element.type == "BlockLinkComponent") {
                    componentRef.instance.title = element.title;
                    componentRef.instance.subtitle = element.subtitle;
                    componentRef.instance.url = element.url;
                    componentRef.instance.external = element.external;
                }
                else if (element.type == "BlockBannerComponent") {
                    let image = {};

                    if (!element.data && !element.images) return;

                    if (_.isString(element.data?.image)) image = { image: element.data?.image };
                    else if (element.data?.image?.url) image = { image: element.data?.image?.url };
                    componentRef.instance.banner = {
                        ...(image),
                        ...(element.data?.url ? { url: element.data?.url } : {}),
                        ...(element.data?.external ? { external: element.data?.external } : {}),
                        ...(element.images) ? { images: element.data?.images || element.images } : {}
                    };
                    componentRef.instance.fullWidth = element.fullWidth || false;
                    componentRef.instance.rounded = element.rounded == "true" || element.rounded == true || false;


                }
                else if (element.type == "BlockSectorSectionComponent") {
                    componentRef.instance.sector = element.sector;
                }
                else if (element.type == "BlockCategoriesSliderComponent") {
                    componentRef.instance.bgColor = element.bgColor;
                    componentRef.instance.fgColor = element.fgColor;
                    componentRef.instance.slides = element.slides;
                    componentRef.instance.categories = element.categories;
                }
                else if (element.type == "BlockPromotionComponent") {
                    componentRef.instance.product = element.data;
                    componentRef.instance.title = element.title;
                    componentRef.instance.caption = element.caption;
                    componentRef.instance.large = element.isLarge == "false" ? false : true;
                    componentRef.instance.tagImage = "assets/svg/" + element.tagStyle;
                    componentRef.instance.backgroundColor = element.backgroundColor;
                    componentRef.instance.foregroundColor = element.foregroundColor;
                }
                else if (element.type == "BlockCategoriesComponent") {
                    componentRef.instance.name = element.title;
                    componentRef.instance.class = element.class;
                    componentRef.instance.mainCategory = element.mainCategory;
                    componentRef.instance.caption = element.caption;
                    componentRef.instance.image = element.image;
                    componentRef.instance.categories = element.categories;
                    componentRef.instance.categoriesCount = element.categoriesCount;
                    componentRef.instance.element = element;

                }
                else if (element.type == "BlockPostsComponent") {
                    componentRef.instance.header = element.header;
                    componentRef.instance.class = element.class;
                    componentRef.instance.posts = element.data;
                    componentRef.instance.items = element.items;
                    componentRef.instance.layout = "slider";

                }
                else if (element.type == "BlockTitleComponent") {
                    componentRef.instance.title = element.title;
                    componentRef.instance.description = element.description;
                } else if (element.type == "BlockSectorsSliderComponent") {
                    componentRef.instance.name = element.name;
                    componentRef.instance.loop = element.loop;
                    componentRef.instance.cssClass = element.cssClass;
                    componentRef.instance.sectors = element.sectors;
                }
            }
            if (!element.agent || ((element.agent?.mobile == "true" && this.agent?.mobile) || (element.agent?.desktop == "true" && this.agent.desktop))) {
                if (element.children) {
                    this.buildLayout(element.children);
                }
            }
        })

        this.cdr.detectChanges();

    }

    private getContainerFor(name: string) {
        return _.find(this.blocks, ["hash", name])?.vcRef;
    }


}
